import React from "react";
import {
  Box,
  Button,
  Paper,
  Portal,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import WestRoundedIcon from "@mui/icons-material/WestRounded";
import { activityApi } from "../../../api/activity";

// WP-STANDARD | LANDING EDITOR GUIDED TOUR
// STATUS: IMPLEMENTED / STATIC_PASS / STAGING_RUNTIME_PENDING
// PURPOSE: preserve the established editor coach UI while extending the same guided
// language into real site editing, starting with the storefront navigation bar.
// SCALE: steps are declarative; future editor capabilities should be added here instead
// of creating one-off overlays. Missing/conditional controls never block the editor.
// PRIVACY: analytics contains structural step keys/timing only, never landing copy,
// form values, customer data, tokens or uploaded asset names.

const OVERLAY_Z = 2147482200;
const PADDING = 8;
const BRAND = { orange: "#F35A3A" };
const STEP_GROUP_LABELS = Object.freeze({ navbar: "Navegación", hero: "Portada", sections: "Secciones" });

const STEPS = Object.freeze([
  // Phase 1 intentionally preserves the orientation that users already learned.
  {
    key: "toolbar",
    group: "orientation",
    selector: '[data-wp-editor-coach-target="toolbar"]',
    title: "Ésta es la cabina del editor",
    body: "Arriba vas a encontrar las acciones globales: cambiar vista, previsualizar, guardar y publicar. Editar y publicar son cosas distintas: podés trabajar tranquilo sobre un borrador.",
  },
  {
    key: "device",
    group: "orientation",
    selector: '[data-wp-editor-coach-target="device"]',
    title: "Revisá siempre cómo se ve en cada pantalla",
    body: "Podés mirar computadora, tablet y celular sin cambiar la web pública. Es una vista de trabajo para evitar que algo quede bien sólo en escritorio.",
  },
  {
    key: "canvas",
    group: "orientation",
    selector: '[data-wp-editor-coach-target="canvas"]',
    title: "El centro es tu página real",
    body: "Hacé click sobre una sección, texto, imagen o botón para editar esa pieza. No necesitás configurar toda la web de una vez: trabajá por partes.",
  },
  {
    key: "guide",
    group: "orientation",
    selector: '[data-wp-editor-coach-target="guide"]',
    title: "WebProfit te propone qué mejorar después",
    body: "La guía inferior mira el avance del borrador y te lleva a la próxima tarea útil. Podés seguirla o ignorarla y editar libremente.",
    optionalTarget: true,
  },
  {
    key: "save",
    group: "orientation",
    selector: '[data-wp-editor-coach-target="save"]',
    title: "Guardar no publica",
    body: "Guardar conserva el borrador. Tus clientes no ven ese cambio todavía. Usalo seguido para trabajar sin miedo.",
  },
  {
    key: "publish",
    group: "orientation",
    selector: '[data-wp-editor-coach-target="publish"]',
    title: "Publicar es el momento de hacerlo visible",
    body: "Cuando estés conforme, Publicar revisa el borrador y recién entonces actualiza la web pública. Ahora sí: veamos cómo se edita una parte real de tu página.",
  },

  // Phase 2 starts the real editor walkthrough with the public navigation bar.
  {
    key: "navbar",
    group: "navbar",
    selector: '[data-landing-section="navbar"]',
    title: "Empecemos por la barra de navegación",
    body: "Es la franja que acompaña a tus visitantes para moverse por la web. Desde acá podés controlar identidad, accesos a secciones, catálogo, botones propios, cuenta del cliente y carrito. En el editor, un click sobre una pieza abre exactamente lo que corresponde editar.",
  },
  {
    key: "navbar-brand",
    group: "navbar",
    selector: '[data-landing-element-id="navbar.logo"], [data-landing-field="business.name"]',
    title: "Tu marca vive acá",
    body: "El logo o el nombre identifican el negocio en todas las páginas. Podés cambiar la imagen, tamaño, superficie y presentación sin tocar el resto de la navegación.",
    optionalTarget: true,
  },
  {
    key: "navbar-links",
    group: "navbar",
    selector: '[data-landing-field="navigation.homeLabel"], [data-landing-field="navigation.aboutLabel"], [data-landing-field="navigation.highlightsLabel"], [data-landing-field="navigation.contactLabel"]',
    title: "Estos enlaces llevan a las partes importantes",
    body: "Inicio, Sobre nosotros, Diferenciales y Contacto pueden mostrarse, ocultarse, renombrarse y ajustar su estilo. Si una sección no tiene sentido para tu negocio, no hace falta forzarla.",
    optionalTarget: true,
  },
  {
    key: "navbar-catalog",
    group: "navbar",
    selector: '[data-landing-field="navigation.productsLabel"]',
    title: "Productos o servicios pueden tener su propia entrada",
    body: "Cuando el proyecto usa catálogo, este acceso conecta la navegación con tus productos o servicios. También puede abrir una experiencia más rica de categorías o mega menú, según cómo armes la web.",
    optionalTarget: true,
  },
  {
    key: "navbar-custom-actions",
    group: "navbar",
    selector: '[data-landing-element-id="navbar.buttons-manager"], [data-navbar-button-id]',
    title: "También podés sumar botones propios",
    body: "Usá botones personalizados para acciones concretas: reservar, pedir presupuesto, abrir una página, destacar una campaña o llevar a otra parte de la web. Administrar botones te permite ordenar esa zona sin rehacer el navbar.",
    optionalTarget: true,
  },
  {
    key: "navbar-commerce-actions",
    group: "navbar",
    selector: '[data-landing-field="navigation.loginLabel"], [data-landing-field="navigation.accountLabel"], [data-landing-field="navigation.cartLabel"]',
    title: "Cuenta y carrito son funciones, no decoración",
    body: "Si tu proyecto las usa, desde acá el visitante entra a su cuenta y al carrito. Podés decidir si se muestran y ajustar cómo se ven sin desconectar la lógica de compra o acceso.",
    optionalTarget: true,
  },
  {
    key: "navbar-mobile",
    group: "navbar",
    selector: '[data-landing-element-id="navbar.mobile-menu"]',
    title: "En celular, la navegación se transforma",
    body: "En pantallas chicas los enlaces se agrupan en el menú móvil. Podés editar el botón, el panel que se abre y su estilo. Siempre conviene revisar esta versión antes de publicar.",
    optionalTarget: true,
  },
  {
    key: "navbar-finish",
    group: "navbar",
    selector: '[data-landing-section="navbar"]',
    title: "La lógica se repite en todo el editor",
    body: "Sección para ajustes generales; elemento para cambios puntuales. Probá haciendo click sobre el logo, un enlace o un botón: WebProfit abre el editor correcto y mantiene el resto de la página intacto.",
  },

  // Phase 3 teaches the first content block and the mental model used by the whole page.
  {
    key: "hero",
    group: "hero",
    selector: '[data-landing-section="hero"]',
    title: "Esta es la portada de tu web",
    body: "Es lo primero que normalmente ve una persona al entrar. Acá resumís qué hace tu negocio, mostrás una imagen fuerte y guiás a la acción principal. No hace falta llenarla de cosas: tiene que entenderse rápido.",
  },
  {
    key: "hero-section-concept",
    group: "hero",
    selector: '[data-landing-section="hero"]',
    title: "La portada es una sección",
    body: "Una sección es un bloque completo de la página. La portada, testimonios, productos, contacto y cualquier bloque que agregues son secciones independientes. Podés editar una sin romper las demás y también decidir en qué orden aparecen.",
  },
  {
    key: "hero-copy",
    group: "hero",
    selector: '[data-landing-field="hero.eyebrow"], [data-landing-field="hero.title"], [data-landing-field^="hero.slides."][data-landing-field$=".title"], [data-landing-field="hero.subtitle"], [data-landing-field^="hero.slides."][data-landing-field$=".subtitle"]',
    title: "Los textos de portada se editan directamente",
    body: "Título, texto superior y subtítulo cuentan la propuesta de tu negocio. Tocá el texto que querés cambiar y WebProfit abre sólo ese editor. Si usás un slider, el título y el subtítulo pertenecen al slide activo, por eso pueden cambiar de una portada a otra.",
    optionalTarget: true,
  },
  {
    key: "hero-image",
    group: "hero",
    selector: '[data-landing-field="hero.imageUrl"], [data-landing-field^="hero.slides."][data-landing-field$=".url"]',
    title: "La imagen también pertenece a esta sección",
    body: "Podés cargar o reemplazar la imagen de portada, ajustar cómo se recorta y revisar el resultado en computadora y celular. Cuando hay slides, cada slide puede tener su propia imagen.",
    optionalTarget: true,
  },
  {
    key: "hero-actions",
    group: "hero",
    selector: '[data-landing-field="hero.primaryCtaLabel"], [data-landing-field="hero.secondaryCtaLabel"]',
    title: "Los botones le dicen al visitante qué hacer",
    body: "El botón principal debería llevar a la acción más importante: comprar, reservar, pedir presupuesto o contactarte. Podés sumar un segundo botón para una alternativa, pero conviene mantener una prioridad clara.",
    optionalTarget: true,
  },

  // Phase 4 explains page structure from the same Add content control users already have.
  {
    key: "sections-entry",
    group: "sections",
    selector: '[data-wp-editor-coach-target="sections-add-entry"]',
    title: "Desde Agregar contenido manejás las secciones",
    body: "Este botón abre la estructura de la página. Desde ahí podés agregar una sección nueva, activar bloques prediseñados, ocultarlos o cambiar el orden. Hacé click en Agregar contenido para ver esa estructura.",
    advanceOnEngage: true,
  },
  {
    key: "sections-add",
    group: "sections",
    selector: '[data-wp-editor-coach-target="add-section-button"]',
    title: "Agregar nueva sección crea un bloque propio",
    body: "Una sección nueva es un bloque libre que después editás directamente en la página: título, imagen, video, botón y diseño. Agregarla prepara el bloque en tu borrador; nada se publica hasta que guardes y publiques.",
  },
  {
    key: "sections-list",
    group: "sections",
    selector: '[data-wp-editor-coach-target="section-list"]',
    title: "Acá ves la estructura completa de tu página",
    body: "Cada fila representa una sección y su posición. También podés mostrar u ocultar bloques sin borrarlos. Navbar y pie de página son estructurales; el contenido del medio es el que podés organizar con libertad.",
  },
  {
    key: "sections-move",
    group: "sections",
    selector: '[data-wp-editor-coach-target="section-move-controls"]',
    title: "Subir y Bajar cambia el orden real",
    body: "Usá Subir o Bajar para decidir qué encuentra primero el visitante. Por ejemplo, podés poner testimonios antes de productos o acercar el contacto a la portada. El cambio queda en el borrador hasta que lo guardes.",
    optionalTarget: true,
  },
  {
    key: "sections-finish",
    group: "sections",
    selector: '[data-wp-editor-coach-target="section-list"]',
    title: "La página se construye sección por sección",
    body: "La idea es simple: elegís una sección, editás sus elementos y ordenás la página según lo que más le conviene a tu negocio. WebProfit va a medir qué partes usan y dónde se traban para poder mejorar este editor con comportamiento real, no sólo con el tutorial.",
    finishLabel: "Terminar tutorial",
  },
]);


// WP-STANDARD | CONTEXTUAL NAVBAR DEEP GUIDE
// STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_RUNTIME_PENDING
// PURPOSE: when the user opens a real Navbar inspector from the main coach, pause the
// main tour and explain the real controls in-place. No duplicate editor state is created.
// SAFETY: returning to the guide closes only the inspector UI; draft values remain in the
// existing editor state and are still governed by the normal Save / Publish contract.
const NAVBAR_DETAIL_GUIDES = Object.freeze({
  shell: Object.freeze([
    { key: "navbar-detail-shell-intro", selector: '[data-wp-navbar-guide-target="inspector-header"]', title: "Entraste a los ajustes generales de la Navbar", body: "Este panel modifica la barra completa, no un enlace puntual. La guía principal quedó pausada: podés recorrer estas opciones y después volver exactamente al punto donde estabas." },
    { key: "navbar-detail-shell-style", selector: '[data-wp-inspector-field="navigation.style"]', title: "Estilo del encabezado", body: "Cambia la variante visual base de la Navbar. Es una decisión estructural: después podés afinar fondo, borde, redondeado y colores sin perder el contenido de los enlaces.", optionalTarget: true },
    { key: "navbar-detail-shell-radius", selector: '[data-wp-inspector-field="navigation.borderRadius"]', title: "Forma exterior de la barra", body: "El redondeado controla la silueta general del encabezado. No cambia los botones ni el menú desplegable: sólo la forma de la superficie principal.", optionalTarget: true },
    { key: "navbar-detail-shell-design", selector: '[data-wp-navbar-guide-target="shell-design"]', title: "Diseño del bloque", body: "Acá viven las superficies y detalles visuales de la Navbar: fondos, bordes, sombras y otros propietarios visuales. Son ajustes del bloque completo, separados del estilo de cada enlace.", optionalTarget: true },
    { key: "navbar-detail-shell-behavior", selector: '[data-wp-navbar-guide-target="shell-behavior"]', title: "Comportamiento", body: "Estas opciones controlan funciones del encabezado, por ejemplo qué elementos aparecen o cómo se comportan. WebProfit mantiene estas decisiones separadas del diseño para que sea más difícil romper la navegación.", optionalTarget: true },
  ]),
  logo: Object.freeze([
    { key: "navbar-detail-logo-intro", selector: '[data-wp-navbar-guide-target="inspector-header"]', title: "Este editor pertenece sólo al logo", body: "Lo que cambies acá afecta la identidad visual del Navbar. No modifica el nombre del negocio ni los enlaces." },
    { key: "navbar-detail-logo-main", selector: '[data-wp-navbar-guide-target="image-main"]', title: "Logo principal", body: "Desde acá reemplazás o quitás el logo usado como base. El cambio queda en el borrador del editor hasta que guardes y publiques." },
    { key: "navbar-detail-logo-mobile", selector: '[data-wp-navbar-guide-target="image-mobile"]', title: "Variante para celular", body: "Podés usar una versión compacta para pantallas chicas. Si no cargás una, WebProfit puede seguir usando el logo principal según la configuración actual.", optionalTarget: true },
    { key: "navbar-detail-logo-appearance", selector: '[data-wp-navbar-guide-target="logo-appearance"]', title: "Apariencia del logo", body: "Acá ajustás tamaño, color cuando el archivo lo permite y la superficie propia del logo. Escritorio y celular tienen propietarios separados para que un ajuste no arruine el otro.", optionalTarget: true },
  ]),
  text: Object.freeze([
    { key: "navbar-detail-text-intro", selector: '[data-wp-navbar-guide-target="inspector-header"]', title: "Estás editando un texto puntual de la Navbar", body: "Este inspector pertenece sólo al elemento que tocaste. La Navbar completa sigue intacta." },
    { key: "navbar-detail-text-copy", selector: '[data-wp-navbar-guide-target="text-content"]', title: "Contenido", body: "Acá cambiás el texto visible. Los límites de longitud y validaciones del editor siguen funcionando igual que fuera del tutorial." },
    { key: "navbar-detail-text-color", selector: '[data-wp-navbar-guide-target="text-color"]', title: "Color propio o heredado", body: "Podés darle un color específico o dejar que herede el estilo de la Navbar. Heredar es útil para mantener consistencia cuando después cambies la paleta.", optionalTarget: true },
  ]),
  button: Object.freeze([
    { key: "navbar-detail-button-intro", selector: '[data-wp-navbar-guide-target="inspector-header"]', title: "Cada enlace o botón tiene su propio editor", body: "Acá definís qué dice, si aparece, a dónde lleva y cómo se ve. La guía sólo ilumina controles reales: si una opción no existe para este botón, se salta sola." },
    { key: "navbar-detail-button-label", selector: '[data-wp-navbar-guide-target="button-label"]', title: "Texto del botón", body: "Es la etiqueta que ve el visitante. Cambiarla no cambia automáticamente el destino: texto y comportamiento son decisiones separadas." },
    { key: "navbar-detail-button-visibility", selector: '[data-wp-navbar-guide-target="button-visibility"]', title: "Mostrar u ocultar", body: "Cuando esta opción existe, podés sacar el acceso de la Navbar sin borrar la sección de la página. Es útil para simplificar el menú sin perder contenido.", optionalTarget: true },
    { key: "navbar-detail-button-behavior", selector: '[data-wp-navbar-guide-target="button-behavior"]', title: "Comportamiento", body: "Los botones personalizados pueden desplazarse a una sección, abrir un link o desplegar un mega menú. Elegí primero el comportamiento y después el destino correspondiente.", optionalTarget: true },
    { key: "navbar-detail-button-destination", selector: '[data-wp-navbar-guide-target="button-destination"]', title: "Destino", body: "Acá decidís qué pasa al tocarlo. WebProfit usa destinos registrados para evitar enlaces internos rotos y permite links externos cuando el tipo de botón lo admite.", optionalTarget: true },
    { key: "navbar-detail-button-mega", selector: '[data-wp-navbar-guide-target="button-mega-menu"]', title: "Mega menú", body: "Si este acceso abre catálogo, esta zona controla su contenido y presentación. Sólo aparece cuando el elemento realmente tiene esa capacidad.", optionalTarget: true },
    { key: "navbar-detail-button-appearance", selector: '[data-wp-navbar-guide-target="button-appearance"]', title: "Apariencia y estados", body: "Podés ajustar colores, hover y estados activos sin cambiar el destino del botón. Desktop y mobile pueden tener estilos independientes cuando el contrato del elemento lo permite.", optionalTarget: true },
  ]),
  buttons: Object.freeze([
    { key: "navbar-detail-buttons-intro", selector: '[data-wp-navbar-guide-target="inspector-header"]', title: "Este panel administra los botones personalizados", body: "Sirve para crear, quitar y ordenar accesos propios de la Navbar. Cada botón conserva su identidad aunque lo reordenes." },
    { key: "navbar-detail-buttons-add", selector: '[data-wp-navbar-guide-target="collection-add"]', title: "Agregar un botón", body: "Crea un nuevo acceso dentro del límite permitido. Después podés tocar ese botón en la página para profundizar en texto, destino y apariencia.", optionalTarget: true },
    { key: "navbar-detail-buttons-items", selector: '[data-wp-navbar-guide-target="collection-items"]', title: "Orden y contenido", body: "Cada tarjeta representa un botón. Podés editar sus datos, moverlo o quitarlo; el resto de la Navbar no se reconfigura por esa acción.", optionalTarget: true },
  ]),
  mobile: Object.freeze([
    { key: "navbar-detail-mobile-intro", selector: '[data-wp-navbar-guide-target="inspector-header"]', title: "La Navbar móvil tiene propietarios propios", body: "En celular la navegación cambia de forma: barra, botón de apertura, drawer y opciones internas pueden necesitar ajustes distintos a escritorio." },
    { key: "navbar-detail-mobile-groups", selector: '[data-wp-navbar-guide-target="mobile-groups"]', title: "Cada grupo controla una parte del menú móvil", body: "Los grupos separan fondo exterior, superficie, drawer, botones, enlaces, cuenta, carrito y catálogo. Abrí sólo el grupo que quieras tocar; no hace falta configurar todos.", optionalTarget: true },
    { key: "navbar-detail-mobile-first", selector: '[data-wp-navbar-mobile-group="0"]', title: "Ajustes por propietario", body: "Dentro de cada grupo aparecen únicamente los controles de esa pieza. Esto evita que un cambio visual del drawer termine afectando la barra o los enlaces por accidente.", optionalTarget: true },
  ]),
});

function navbarDetailGuideKey(selectedElement, shellSectionId = "") {
  const sectionType = String(selectedElement?.sectionType || selectedElement?.sectionId || shellSectionId || "");
  if (sectionType !== "navbar") return "";
  if (shellSectionId === "navbar" && !selectedElement) return "shell";
  const elementId = String(selectedElement?.elementId || "");
  const fieldPath = String(selectedElement?.fieldPath || "");
  if (elementId === "navbar.mobile-menu") return "mobile";
  if (elementId === "navbar.buttons-manager") return "buttons";
  if (elementId === "navbar.logo" || fieldPath === "theme.logoUrl" || fieldPath === "theme.compactLogoUrl") return "logo";
  if (fieldPath === "business.name") return "text";
  if (/^navigation\.(?:buttons\.\d+\.|homeLabel$|aboutLabel$|highlightsLabel$|productsLabel$|contactLabel$|loginLabel$|accountLabel$|cartLabel$)/.test(fieldPath)) return "button";
  return "shell";
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function visibleElement(selector) {
  if (!selector || typeof document === "undefined") return null;
  try {
    return Array.from(document.querySelectorAll(selector)).find((node) => {
      if (!(node instanceof HTMLElement)) return false;
      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);
      return rect.width > 2 && rect.height > 2 && style.display !== "none" && style.visibility !== "hidden";
    }) || null;
  } catch {
    return null;
  }
}

function blocker(extra = {}) {
  return {
    position: "fixed",
    zIndex: OVERLAY_Z,
    bgcolor: "rgba(6,8,12,.78)",
    pointerEvents: "auto",
    backdropFilter: "blur(1.5px)",
    ...extra,
  };
}

function Spotlight({ rect }) {
  if (!rect || typeof window === "undefined") return <Box sx={blocker({ inset: 0 })} />;
  const width = window.innerWidth;
  const height = window.innerHeight;
  const left = clamp(rect.left - PADDING, 0, width);
  const top = clamp(rect.top - PADDING, 0, height);
  const right = clamp(rect.right + PADDING, 0, width);
  const bottom = clamp(rect.bottom + PADDING, 0, height);
  return (
    <>
      <Box sx={blocker({ left: 0, top: 0, width: "100vw", height: top })} />
      <Box sx={blocker({ left: 0, top, width: left, height: Math.max(0, bottom - top) })} />
      <Box sx={blocker({ left: right, top, width: Math.max(0, width - right), height: Math.max(0, bottom - top) })} />
      <Box sx={blocker({ left: 0, top: bottom, width: "100vw", height: Math.max(0, height - bottom) })} />
      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          zIndex: OVERLAY_Z + 1,
          pointerEvents: "none",
          left,
          top,
          width: Math.max(1, right - left),
          height: Math.max(1, bottom - top),
          borderRadius: 2.6,
          border: "2px solid rgba(243,90,58,.96)",
          boxShadow: "0 0 0 5px rgba(243,90,58,.17), 0 0 42px rgba(243,90,58,.34)",
        }}
      />
    </>
  );
}

function track(project, action, metadata = {}, sessionId = "") {
  if (!project) return;
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  activityApi.trackPanel({
    action,
    projectId: String(project?.id || project?._id || ""),
    projectSlug: String(project?.slug || ""),
    projectName: String(project?.name || ""),
    path,
    journey: {
      channel: "panel",
      sessionId,
      path,
      step: Number(metadata.stepIndex || 0),
    },
    metadata: {
      analyticsOnly: true,
      telemetryVersion: 2,
      tutorialKey: "landing_editor_guided_tour",
      tutorialLabel: "Editor web",
      tutorialSource: String(metadata.source || "editor"),
      stageKey: String(metadata.stepKey || "editor"),
      totalSteps: STEPS.length,
      ...metadata,
    },
  }).catch(() => null);
}

export default function LandingEditorIntroCoach({
  open = false,
  project,
  source = "editor",
  inspectorOpen = false,
  selectedElement = null,
  shellSectionId = "",
  onExitInspector,
  onClose,
  onComplete,
}) {
  const [stepIndex, setStepIndex] = React.useState(0);
  const [detailGuideKey, setDetailGuideKey] = React.useState("");
  const [detailStepIndex, setDetailStepIndex] = React.useState(0);
  const [target, setTarget] = React.useState(null);
  const [rect, setRect] = React.useState(null);
  const startedAtRef = React.useRef(Date.now());
  const stepStartedAtRef = React.useRef(Date.now());
  const sessionIdRef = React.useRef("");
  const engagedStepRef = React.useRef("");
  const detailArmedFromStepRef = React.useRef("");

  const detailSteps = detailGuideKey ? (NAVBAR_DETAIL_GUIDES[detailGuideKey] || []) : [];
  const detailMode = detailSteps.length > 0;
  const activeSteps = detailMode ? detailSteps : STEPS;
  const activeIndex = detailMode ? detailStepIndex : stepIndex;
  const step = activeSteps[activeIndex] || activeSteps[0] || STEPS[stepIndex] || STEPS[0];
  const trackingLayer = detailMode ? "navbar_detail" : "main";

  React.useEffect(() => {
    if (!open) return;
    setStepIndex(0);
    setDetailGuideKey("");
    setDetailStepIndex(0);
    detailArmedFromStepRef.current = "";
    startedAtRef.current = Date.now();
    stepStartedAtRef.current = Date.now();
    engagedStepRef.current = "";
    sessionIdRef.current = `landing-editor-guide:${String(project?.slug || project?.id || project?._id || "project")}:${Date.now()}`;
    track(project, "onboarding_editor_orientation_started", {
      source,
      stepIndex: 0,
      stepKey: STEPS[0]?.key || "toolbar",
      stepGroup: STEPS[0]?.group || "orientation",
      orientationResult: "started",
      tutorialLayer: "main",
    }, sessionIdRef.current);
  }, [open, project, source]);

  // Enter the contextual guide only when a Navbar target was engaged from this coach.
  // Manually opening an inspector while the tour is running does not hijack the tour.
  React.useEffect(() => {
    if (!open || detailMode || !inspectorOpen || !detailArmedFromStepRef.current) return;
    const nextGuideKey = navbarDetailGuideKey(selectedElement, shellSectionId);
    if (!nextGuideKey || !(NAVBAR_DETAIL_GUIDES[nextGuideKey] || []).length) return;
    setDetailGuideKey(nextGuideKey);
    setDetailStepIndex(0);
    setTarget(null);
    setRect(null);
    track(project, "onboarding_editor_orientation_step", {
      source,
      stepIndex,
      stepKey: `navbar-detail:${nextGuideKey}`,
      stepGroup: "navbar",
      orientationResult: "detail_started",
      tutorialLayer: "navbar_detail",
      parentStepKey: detailArmedFromStepRef.current,
      detailGuideKey: nextGuideKey,
    }, sessionIdRef.current);
  }, [detailMode, inspectorOpen, open, project, selectedElement, shellSectionId, source, stepIndex]);

  // If the user closes the inspector directly, gracefully resume the parent guide.
  React.useEffect(() => {
    if (!open || !detailMode || inspectorOpen) return;
    track(project, "onboarding_editor_orientation_step", {
      source,
      stepIndex,
      stepKey: step?.key || `navbar-detail:${detailGuideKey}`,
      stepGroup: "navbar",
      orientationResult: "detail_inspector_closed",
      tutorialLayer: "navbar_detail",
      parentStepKey: detailArmedFromStepRef.current,
      detailGuideKey,
    }, sessionIdRef.current);
    setDetailGuideKey("");
    setDetailStepIndex(0);
    detailArmedFromStepRef.current = "";
  }, [detailGuideKey, detailMode, inspectorOpen, open, project, source, step?.key, stepIndex]);

  React.useEffect(() => {
    if (!open || !step) return undefined;
    let misses = 0;
    const locate = () => {
      const found = visibleElement(step.selector);
      if (!found) {
        misses += 1;
        setTarget(null);
        setRect(null);
        if (step.optionalTarget && misses >= 8) {
          track(project, "onboarding_editor_orientation_step", {
            source,
            stepIndex: activeIndex,
            stepKey: step.key,
            stepGroup: detailMode ? "navbar" : step.group,
            orientationResult: "target_missing_skipped",
            dwellMs: Math.max(0, Date.now() - stepStartedAtRef.current),
            tutorialLayer: trackingLayer,
            parentStepKey: detailMode ? detailArmedFromStepRef.current : "",
            detailGuideKey: detailMode ? detailGuideKey : "",
            totalSteps: activeSteps.length,
          }, sessionIdRef.current);
          if (detailMode) {
            setDetailStepIndex((current) => Math.min(activeSteps.length - 1, current + 1));
          } else {
            setStepIndex((current) => Math.min(STEPS.length - 1, current + 1));
          }
        }
        return;
      }
      misses = 0;
      setTarget(found);
      const nextRect = found.getBoundingClientRect();
      setRect(nextRect);
      if (nextRect.top < 78 || nextRect.bottom > window.innerHeight - 78) {
        found.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    };
    locate();
    const timer = window.setInterval(locate, 180);
    const update = () => {
      if (!target?.isConnected) return;
      setRect(target.getBoundingClientRect());
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [activeIndex, activeSteps.length, detailGuideKey, detailMode, open, project, source, step, target, trackingLayer]);

  React.useEffect(() => {
    if (!open || !step) return;
    stepStartedAtRef.current = Date.now();
    engagedStepRef.current = "";
    track(project, "onboarding_editor_orientation_step", {
      source,
      stepIndex: activeIndex,
      stepKey: step.key,
      stepGroup: detailMode ? "navbar" : step.group,
      orientationResult: "viewed",
      tutorialLayer: trackingLayer,
      parentStepKey: detailMode ? detailArmedFromStepRef.current : "",
      detailGuideKey: detailMode ? detailGuideKey : "",
      totalSteps: activeSteps.length,
    }, sessionIdRef.current);
  }, [activeIndex, activeSteps.length, detailGuideKey, detailMode, open, project, source, step, trackingLayer]);

  React.useEffect(() => {
    if (!open || !target || !step) return undefined;
    const onTargetPointerDown = (event) => {
      if (!target.contains(event.target) || engagedStepRef.current === step.key) return;
      engagedStepRef.current = step.key;
      const dwellMs = Math.max(0, Date.now() - stepStartedAtRef.current);
      track(project, "onboarding_editor_orientation_target_engaged", {
        source,
        stepIndex: activeIndex,
        stepKey: step.key,
        stepGroup: detailMode ? "navbar" : step.group,
        orientationResult: "target_engaged",
        dwellMs,
        tutorialLayer: trackingLayer,
        parentStepKey: detailMode ? detailArmedFromStepRef.current : "",
        detailGuideKey: detailMode ? detailGuideKey : "",
        totalSteps: activeSteps.length,
      }, sessionIdRef.current);

      if (!detailMode && step.group === "navbar") {
        detailArmedFromStepRef.current = step.key;
      }

      if (step.advanceOnEngage && !detailMode && stepIndex < STEPS.length - 1) {
        track(project, "onboarding_editor_orientation_step", {
          source,
          stepIndex,
          stepKey: step.key,
          stepGroup: step.group,
          orientationResult: "completed",
          completionMethod: "target_engaged",
          dwellMs,
          tutorialLayer: "main",
        }, sessionIdRef.current);
        window.setTimeout(() => setStepIndex((current) => Math.min(STEPS.length - 1, current + 1)), 180);
      }
    };
    document.addEventListener("pointerdown", onTargetPointerDown, true);
    return () => document.removeEventListener("pointerdown", onTargetPointerDown, true);
  }, [activeIndex, activeSteps.length, detailGuideKey, detailMode, open, project, source, step, stepIndex, target, trackingLayer]);

  if (!open || !step) return null;

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardWidth = Math.min(440, Math.max(300, viewportWidth - 24));
  const estimatedHeight = detailMode ? 330 : 300;
  const canRight = Boolean(rect && rect.right + cardWidth + 22 <= viewportWidth);
  const canLeft = Boolean(rect && rect.left - cardWidth - 22 >= 0);
  const left = rect
    ? canRight
      ? rect.right + 14
      : canLeft
        ? rect.left - cardWidth - 14
        : clamp((viewportWidth - cardWidth) / 2, 12, viewportWidth - cardWidth - 12)
    : Math.max(12, (viewportWidth - cardWidth) / 2);
  const top = rect
    ? (canRight || canLeft)
      ? clamp(rect.top, 12, Math.max(12, viewportHeight - estimatedHeight - 12))
      : rect.bottom + estimatedHeight + 24 <= viewportHeight
        ? rect.bottom + 12
        : clamp(rect.top - estimatedHeight - 12, 12, Math.max(12, viewportHeight - estimatedHeight - 12))
    : Math.max(12, viewportHeight / 2 - estimatedHeight / 2);

  const returnToGuide = (reason = "returned") => {
    if (!detailMode) return;
    track(project, "onboarding_editor_orientation_step", {
      source,
      stepIndex: detailStepIndex,
      stepKey: step.key,
      stepGroup: "navbar",
      orientationResult: reason,
      dwellMs: Math.max(0, Date.now() - stepStartedAtRef.current),
      tutorialLayer: "navbar_detail",
      parentStepKey: detailArmedFromStepRef.current,
      detailGuideKey,
      totalSteps: detailSteps.length,
    }, sessionIdRef.current);
    setDetailGuideKey("");
    setDetailStepIndex(0);
    detailArmedFromStepRef.current = "";
    setTarget(null);
    setRect(null);
    onExitInspector?.();
  };

  const close = () => {
    track(project, "onboarding_editor_orientation_completed", {
      source,
      stepIndex,
      stepKey: detailMode ? `${detailGuideKey}:${step.key}` : step.key,
      stepGroup: detailMode ? "navbar" : step.group,
      orientationResult: "closed",
      dwellMs: Math.max(0, Date.now() - startedAtRef.current),
      tutorialLayer: trackingLayer,
      parentStepKey: detailMode ? detailArmedFromStepRef.current : "",
      detailGuideKey: detailMode ? detailGuideKey : "",
    }, sessionIdRef.current);
    if (detailMode) onExitInspector?.();
    onClose?.();
  };

  const next = () => {
    track(project, "onboarding_editor_orientation_step", {
      source,
      stepIndex: activeIndex,
      stepKey: step.key,
      stepGroup: detailMode ? "navbar" : step.group,
      orientationResult: "completed",
      dwellMs: Math.max(0, Date.now() - stepStartedAtRef.current),
      tutorialLayer: trackingLayer,
      parentStepKey: detailMode ? detailArmedFromStepRef.current : "",
      detailGuideKey: detailMode ? detailGuideKey : "",
      totalSteps: activeSteps.length,
    }, sessionIdRef.current);

    if (detailMode) {
      if (detailStepIndex >= detailSteps.length - 1) {
        returnToGuide("detail_completed");
        return;
      }
      setDetailStepIndex((current) => Math.min(detailSteps.length - 1, current + 1));
      return;
    }

    detailArmedFromStepRef.current = "";
    if (stepIndex >= STEPS.length - 1) {
      track(project, "onboarding_editor_orientation_completed", {
        source,
        stepIndex,
        stepKey: step.key,
        stepGroup: step.group,
        orientationResult: "completed",
        dwellMs: Math.max(0, Date.now() - startedAtRef.current),
        tutorialLayer: "main",
      }, sessionIdRef.current);
      onComplete?.();
      return;
    }
    setStepIndex((current) => Math.min(STEPS.length - 1, current + 1));
  };

  const previous = () => {
    if (detailMode) {
      setDetailStepIndex((current) => Math.max(0, current - 1));
      return;
    }
    detailArmedFromStepRef.current = "";
    setStepIndex((current) => Math.max(0, current - 1));
  };

  return (
    <Portal>
      <Spotlight rect={target && rect ? rect : null} />
      <Paper
        role="dialog"
        aria-label={detailMode ? "Guía contextual de la barra de navegación" : "Orientación del editor web"}
        elevation={24}
        sx={{
          position: "fixed",
          zIndex: OVERLAY_Z + 8,
          top,
          left,
          width: cardWidth,
          maxHeight: "calc(100dvh - 24px)",
          overflowY: "auto",
          p: { xs: 1.7, sm: 2.1 },
          borderRadius: 3.5,
          bgcolor: "rgba(18,20,24,.985)",
          color: "#fff",
          border: "1px solid rgba(243,90,58,.34)",
          boxShadow: "0 32px 100px rgba(0,0,0,.58)",
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={.8} alignItems="center">
              <AutoAwesomeRoundedIcon sx={{ color: BRAND.orange, fontSize: 20 }} />
              <Typography sx={{ fontSize: 10.8, fontWeight: 1000, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.58)" }}>
                {detailMode
                  ? `Navbar · dentro del editor · ${detailStepIndex + 1}/${detailSteps.length}`
                  : `Editor · ${STEP_GROUP_LABELS[step.group] ? `${STEP_GROUP_LABELS[step.group]} · ` : ""}${stepIndex + 1}/${STEPS.length}`}
              </Typography>
            </Stack>
            <Button startIcon={<CloseRoundedIcon />} size="small" onClick={close} sx={{ minWidth: 0, color: "rgba(255,255,255,.55)", textTransform: "none", fontWeight: 800 }}>
              Cerrar
            </Button>
          </Stack>

          {detailMode ? (
            <Box sx={{ px: 1, py: .7, borderRadius: 2, bgcolor: "rgba(243,90,58,.10)", border: "1px solid rgba(243,90,58,.18)" }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,.68)", fontWeight: 850 }}>
                La guía principal está pausada. Podés probar estos controles y volver cuando quieras.
              </Typography>
            </Box>
          ) : null}

          <Box>
            <Typography sx={{ fontSize: { xs: 21, sm: 24 }, fontWeight: 1000, lineHeight: 1.12 }}>
              {step.title}
            </Typography>
            <Typography sx={{ mt: .75, fontSize: 13.5, lineHeight: 1.6, color: "rgba(255,255,255,.75)" }}>
              {step.body}
            </Typography>
          </Box>

          {!target ? (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,.46)" }}>
              Esta opción no está visible con la configuración actual. Podés continuar sin bloquear el editor.
            </Typography>
          ) : null}

          <Stack direction="row" spacing={.8} useFlexGap flexWrap="wrap">
            <Button
              variant="contained"
              endIcon={<EastRoundedIcon />}
              onClick={next}
              sx={{ bgcolor: BRAND.orange, color: "#17181C", textTransform: "none", fontWeight: 1000, boxShadow: "none", "&:hover": { bgcolor: "#FF714F" } }}
            >
              {detailMode
                ? (detailStepIndex >= detailSteps.length - 1 ? "Volver a la guía" : "Siguiente opción")
                : (stepIndex >= STEPS.length - 1 ? (step.finishLabel || "Terminar tutorial") : "Siguiente")}
            </Button>
            {(detailMode || stepIndex > 0) ? (
              <Button
                startIcon={<WestRoundedIcon />}
                onClick={detailMode && detailStepIndex === 0 ? () => returnToGuide("returned_early") : previous}
                sx={{ color: "rgba(255,255,255,.65)", textTransform: "none", fontWeight: 850 }}
              >
                {detailMode && detailStepIndex === 0 ? "Volver a la guía" : "Anterior"}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Paper>
    </Portal>
  );
}
